import { useParams, useSearchParams } from "react-router-dom";
import { Box } from "@mui/material";
import { Loading, LoadingFailed } from "~/components/loading";
import { Space } from "~/lib/types";
import {
  AggregateScoreType,
  CaseStatus,
  DocumentType,
  ExceptionType,
  FindingType,
  GetAffectedAssetsDocument,
  GetAggregateScoresDocument,
  GetCasesCountDocument,
  ScoreRating,
  UiPage,
  ScoreState,
  ScoreStateFilter,
  useGetAggregateScoresQuery,
  useGetCasesCountQuery,
  useLoadCveQuery,
} from "~/operations";
import { CveRiskFactors, CveSources } from "~/components/vulnerabilities";
import { FlagOutlined, Radar } from "@mui/icons-material";
import { useInventory } from "~/pages/inventory/hooks";
import { StatsProps } from "~/components/DetailsPage/components/Stats/Stats";
import { SectionHeading } from "~/components/DetailsPage/components";

import { useFindingRiskFactors } from "../security/components/RiskFactors/hooks/useFindingRiskFactors";
import { ScoreBlock } from "~/pages/space/security/components/Check/ScoreBlock";
import { SpaceVulnerabilitiesAdvisoriesTable } from "~/pages/space/vulnerabilities/components/SpaceVulnerabilitiesAdvisoriesTable";

import { ScopeType, SpaceOrWorkspaceScope } from "~/hooks/useScope";
import { useReportViewed } from "~/hooks/useReportViewed";
import { DetectedBy } from "~/components/vulnerabilities/DetectedBy/DetectedBy";
import { DetailPageTopSection } from "~/components/DetailPageLayouts/DetailPageTopSection";
import { GenerateReportButton } from "~/components/GenerateReport/GenerateReportButton";
import {
  ExposedAssets,
  ExposedAssetsAdvisoryNode,
  ExposedAssetsCVENode,
  AssetContextualLinkType,
  SelectedEntityWithExposedAssetData,
} from "~/pages/space/vulnerabilities/components/exposed-assets";
import { InventoryPageHeader } from "~/routes";
import { useExceptions } from "~/components/exceptions/use-exceptions";
import { useTickets } from "~/pages/tickets/hooks";
import { ChangeEvent, useEffect } from "react";
import { setDocumentTitle } from "~/lib/commonUtils";
import { ScrollToTop } from "~/lib/scroll-to-top";
import { getExceptionLink } from "~/pages/findings/Exceptions/utils";
import { TicketsSummary } from "~/pages/tickets/components/TicketsSummary/TicketsSummary";
import { ExposedAsset } from "~/pages/space/vulnerabilities/components/exposed-assets/types";
import { Remediation } from "~/components/Remediation/Remediation";
import { useDialogs } from "@toolpad/core";
import { RemoveExceptionsGroupDialog } from "~/components/exceptions/RemoveExceptionsGroupDialog";
import {
  SelectedEntityWithFindingData,
  SetExceptionDialog,
} from "~/components/exceptions/SetExceptionDialog";
import { CreateTicketDialog } from "~/pages/tickets/components/CreateTicketDialog";
import { useFeatureFlags } from "~/providers/FeatureFlagsProvider";
import { useSelection } from "~/hooks/useSelection";
import { useTicketingIntegrations } from "~/pages/tickets/hooks/useTicketingIntegrations";
import { SCORE_STATE_FILTER_KEY } from "~/pages/inventory/asset/useAssetVulnerabilitiesFilterContext";
import { HealingIcon } from "~/icons";
import { CreateSecurityPipelinePrDialog } from "../../integrations/security-pipeline/components/CreatePullRequest/CreateSecurityPipelinePRDialog";
import { flattenExposedAssetsFindings } from "../../integrations/security-pipeline/components/CreatePullRequest/utils/flattenExposedAssetsFindings";
import { useSecurityPipelineIntegrations } from "~/pages/integrations/security-pipeline/hooks/useSecurityPipelineIntegrations";
import { SelectedEntityWithException } from "~/components/exceptions/types";

interface SpaceVulnerabilityPageProps {
  space: Space;
  scope: SpaceOrWorkspaceScope;
}

export const SpaceVulnerabilityPage = ({
  space,
  scope,
}: SpaceVulnerabilityPageProps) => {
  const { id = "" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const advisoryId = searchParams.get("advisoryId");
  const { assetsTotalCount } = useInventory({ scope });
  const dialogs = useDialogs();
  const { isFeatureEnabled } = useFeatureFlags();
  const { data, loading, error } = useLoadCveQuery({
    variables: { id },
  });

  useReportViewed({
    page: UiPage.Finding,
    pageLoaded: !loading && data !== undefined,
    assetMrn: data?.cve?.mrn,
    tab: "cve",
  });

  const { data: aggScoreData, loading: aggScoreLoading } =
    useGetAggregateScoresQuery({
      variables: {
        entityMrn: scope.mrn || "",
        filter: {
          findingMrn: data?.cve?.mrn,
        },
      },
      skip: !data?.cve?.mrn || !scope.mrn,
    });

  const { riskFactorsWithDocs, loading: riskFactorsLoading } =
    useFindingRiskFactors({
      spaceMrn: space.mrn,
      findingMrn: String(data?.cve?.mrn || ""),
      scoreType: AggregateScoreType.Vulnerability,
      onlyAffected: false,
    });

  const {
    handleCreateCase,
    handleCreateTicketingIntegrationModalOpen,
    hasCreateTicketPermissions,
    hasListTicketsPermissions,
    loading: createTicketLoading,
  } = useTickets({
    createCaseRefetchQueries: [GetCasesCountDocument],
    onTicketCreate: () => setSelectedAffectedAssets([]),
  });

  const { data: tickets } = useGetCasesCountQuery({
    variables: {
      input: {
        scopeMrn: scope.mrn,
        findingMrns: [data?.cve?.mrn as string],
        statuses: [CaseStatus.Open],
      },
    },
    fetchPolicy: "cache-and-network",
    skip: !data?.cve?.mrn || !hasListTicketsPermissions,
  });

  useEffect(() => {
    searchParams.set(SCORE_STATE_FILTER_KEY, ScoreState.Open);
    setSearchParams(searchParams, { replace: true });
  }, []);

  const { hasTicketingIntegration } = useTicketingIntegrations();
  const { hasSecurityPipelineIntegrations } = useSecurityPipelineIntegrations();

  const {
    selectedEntities: selectedAffectedAssets,
    setSelectedEntities: setSelectedAffectedAssets,
    handleNodeClick,
    handleCancelClick,
  } = useSelection<
    SelectedEntityWithExposedAssetData &
      SelectedEntityWithException &
      SelectedEntityWithFindingData
  >();

  const {
    handleSetExceptionForScopes,
    handleRemoveExceptionGroups,
    loading: exceptionsLoading,
  } = useExceptions({
    onSetException: () => {
      setSelectedAffectedAssets([]);
    },
    multipleScopes: true,
    scopeMrns:
      selectedAffectedAssets.length > 0
        ? selectedAffectedAssets.map((s) => s.scopeMrn)
        : [scope.mrn],
    cveMrns: [data?.cve?.mrn || ""],
    refetchQueries: [GetAggregateScoresDocument, GetAffectedAssetsDocument],
  });

  const cve = data?.cve;
  const aggScoreEdges =
    aggScoreData?.aggregateScores?.__typename === "AggregateScoresConnection"
      ? aggScoreData.aggregateScores.edges
      : [];
  const aggScore =
    aggScoreData?.aggregateScores?.__typename === "AggregateScoresConnection"
      ? aggScoreEdges?.at(0)?.node
      : undefined;

  if (loading || aggScoreLoading || riskFactorsLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          width: "100%",
        }}
      >
        <Loading what="Vulnerability" />
      </Box>
    );
  }

  // When no aggregateScore exists, we fallback to the cvssScore of the CVE.
  let fallbackCvssScore = undefined;
  if (!aggScore) {
    if (cve?.cvssScore.value) {
      // If the CVE has no cvssScore - the scoreblock will fallback further to UNKNOWN
      fallbackCvssScore = cve?.cvssScore;
    }
  }

  if (error || !cve) {
    return <LoadingFailed what="Vulnerability" />;
  }

  const totalScanned = assetsTotalCount;
  const totalAffected = aggScore?.blastRadius?.affected || 0;
  const totalRemediated =
    aggScore?.blastRadius?.assets && aggScore?.blastRadius?.affected
      ? (
          aggScore?.blastRadius?.assets - aggScore.blastRadius?.affected
        ).toString()
      : "---";

  const stats: StatsProps["stats"] = [
    {
      label: "Scanned",
      value: totalScanned < 0 ? "---" : totalScanned.toString(),
      icon: <Radar fontSize="inherit" />,
    },
    {
      label: "Exposed",
      value: totalAffected < 0 ? "---" : totalAffected.toString(),
      icon: <FlagOutlined fontSize="inherit" />,
      onClick: () => {
        document
          .querySelector(`#affected-assets`)
          ?.scrollIntoView({ behavior: "smooth" });
      },
    },
    {
      label: "Remediated",
      value: totalRemediated,
      icon: <HealingIcon fontSize="inherit" />,
    },
    // {
    //   label: "Exceptions",
    //   value: totalExceptions,
    //   icon: <BlockOutlined fontSize="inherit" />,
    // },
    // {
    //   label: "Updated",
    //   count: -1, // TODO
    //   icon: <ZoomOutMap fontSize="inherit" />,
    // },
  ];

  const handleCreateTicketDialogOpen = ({
    selectedAssets,
    isScopeLevelTicket,
  }: {
    selectedAssets: SelectedEntityWithExposedAssetData[];
    isScopeLevelTicket: boolean;
  }) => {
    dialogs.open(CreateTicketDialog, {
      isCombinedTicket: true,
      target: "asset",
      loading: loading || createTicketLoading,
      space: space,
      scope: scope,
      hasCreateCasePermissions: hasCreateTicketPermissions,
      onSave: (data) =>
        handleCreateCase({
          data,
          refetchQueries: [GetAggregateScoresDocument],
        }),
      selectedEntities: selectedAssets.map((s) => ({
        ...s,
        mrn: cve.mrn,
      })),
      assetsCount: isScopeLevelTicket ? totalAffected : selectedAssets.length,
    });
  };

  const handleSecurityPipelinePRDialogOpen = () => {
    dialogs.open(CreateSecurityPipelinePrDialog, {
      aggregateScoresEdges: aggScoreEdges,
      spaceScope: space,
    });
  };

  const handleFindingsSecurityPipelinePRDialogOpen = ({
    selectedAssets,
    findings,
  }: {
    selectedAssets: SelectedEntityWithExposedAssetData[];
    findings: Array<ExposedAssetsAdvisoryNode | ExposedAssetsCVENode>;
  }) => {
    dialogs.open(CreateSecurityPipelinePrDialog, {
      findings: flattenExposedAssetsFindings({ findings }),
      spaceScope: space,
      assetMrns: selectedAssets.map((s) => s.scopeMrn),
    });
  };

  const handleSetExceptionDialogOpen = ({
    selectedAssets,
  }: {
    selectedAssets: Array<
      SelectedEntityWithExposedAssetData &
        SelectedEntityWithException &
        SelectedEntityWithFindingData
    >;
  }) => {
    dialogs.open(SetExceptionDialog, {
      target: "cve",
      selectedEntities: selectedAssets,
      targetData: {
        targetTitle: cve.id || "",
      },
      onSave: handleSetExceptionForScopes,
    });
  };

  const handleRemoveExceptionDialogOpen = ({
    selectedAssets,
  }: {
    selectedAssets: Array<
      SelectedEntityWithExposedAssetData &
        SelectedEntityWithException &
        SelectedEntityWithFindingData
    >;
  }) => {
    dialogs.open(RemoveExceptionsGroupDialog, {
      onRemove: async (params) => {
        await handleRemoveExceptionGroups(params);
        setSelectedAffectedAssets([]);
      },
      exceptionMrns: selectedAssets
        .map((a) => a.exception?.exceptionMrn)
        .filter((mrn): mrn is string => Boolean(mrn)),
    });
  };

  setDocumentTitle([cve.id, "CVEs"]);

  return (
    <>
      <ScrollToTop />
      <DetailPageTopSection
        content={{
          triageBar: {
            title: cve.id,
            fallbackPath: advisoryId
              ? `/space/vulns/advisory/${advisoryId}?${scope.params}`
              : `/space/findings/cves?${scope.params}`,
            triage: true,
            subheaders: [
              {
                id: "created",
                label: "Created at",
                value: String(cve.publishedAt),
              },
              {
                id: "last-modified",
                label: "Last modified",
                value: String(cve.modifiedAt),
              },
            ],
            toolbarItems: [
              {
                id: "generate-report",
                component: (
                  <GenerateReportButton
                    documentType={DocumentType.VulnerabilityReport}
                    title={cve.id || "Generated Vulnerability Report"}
                    space={space}
                    vulnerabilityId={cve.id}
                    IconProps={{ sx: { color: "text.primary" } }}
                  />
                ),
              },
            ],
            isLoading: exceptionsLoading || createTicketLoading,
            availableRemediations: {
              ticket: {
                active: true,
                onClick: hasTicketingIntegration
                  ? () =>
                      handleCreateTicketDialogOpen({
                        selectedAssets: [
                          {
                            mrn: cve?.mrn,
                            scopeMrn: scope.mrn,
                            findingMrn: cve?.mrn || "",
                          },
                        ],
                        isScopeLevelTicket: true,
                      })
                  : handleCreateTicketingIntegrationModalOpen,
              },
              securityPipeline: {
                active:
                  isFeatureEnabled("Security Pipeline") &&
                  hasSecurityPipelineIntegrations,
                onClick: handleSecurityPipelinePRDialogOpen,
              },
              exception: {
                active: scope.type === ScopeType.Space,
                hasException: !!aggScore?.exception,
                linkToException: getExceptionLink({
                  id: aggScore?.exception?.exceptionId || "",
                  queryParams: scope.params,
                }),
                onClick: !!aggScore?.exception
                  ? () => {
                      handleRemoveExceptionDialogOpen({
                        selectedAssets: [
                          {
                            mrn: scope?.mrn || "",
                            scopeMrn: scope.mrn,
                            findingMrn: cve.mrn,
                            title: cve.id,
                            rating: aggScore?.rating || ScoreRating.Unscored,
                            exception: aggScore?.exception,
                          },
                        ],
                      });
                    }
                  : () => {
                      handleSetExceptionDialogOpen({
                        selectedAssets: [
                          {
                            mrn: scope?.mrn || "",
                            scopeMrn: scope.mrn,
                            findingMrn: cve.mrn,
                            title: cve.id,
                            rating: aggScore?.rating || ScoreRating.Unscored,
                            exception: aggScore?.exception,
                          },
                        ],
                      });
                    },
                disabled: exceptionsLoading,
              },
            },
            conversation: {
              scope: scope,
              findingMrn: cve.mrn,
              findingType: "cvefinding",
            },
          },
          ...(hasListTicketsPermissions &&
            (tickets?.cases.totalCount || 0 > 0) && {
              ticketsSummary: (
                <TicketsSummary
                  count={tickets?.cases.totalCount || 0}
                  scope={scope}
                />
              ),
            }),
          summary: cve.summary,
          stats: stats,
          left: {
            upper: <DetectedBy detectedBy={aggScore?.detectionSources} />,
            lower: (
              <CveSources
                id="cve-sources"
                url={cve.url}
                source={cve.source}
                externalUrls={cve.externalUrls}
                references={cve.references}
              />
            ),
          },
          right: (
            <ScoreBlock
              hasScore={Boolean(aggScore)}
              mainScore={aggScore?.riskValue}
              cvssScore={aggScore?.cvss || fallbackCvssScore}
              epssScore={aggScore?.epss}
              rating={aggScore?.rating || ScoreRating.Unscored}
              blastRadius={aggScore?.blastRadius}
              riskFactors={aggScore?.riskFactors}
              hasError={
                Boolean(aggScore) && aggScore?.__typename !== "AggregateScore"
              }
            />
          ),
          openExceptions: {
            scopeMrn: scope.mrn,
            exceptionId: aggScore?.exception?.exceptionId,
            types: [ExceptionType.Cve],
          },
        }}
      />
      <InventoryPageHeader>
        <Box id="risk-factors" className="section">
          <SectionHeading heading="Vulnerability insights" />
          <Box className="section-content">
            <CveRiskFactors
              id="cve-risk-factors"
              cvssScore={cve.cvssScore}
              epssScore={cve.epssScore}
              riskFactors={riskFactorsWithDocs}
            />
          </Box>
        </Box>

        <Remediation
          findingId={cve.id}
          scopeMrn={scope.mrn}
          findingMrn={cve.mrn}
        />

        <Box id="cve-advisories" className="section">
          <SectionHeading heading="Related advisories" />
          <Box className="section-content">
            <SpaceVulnerabilitiesAdvisoriesTable
              space={space}
              scope={scope}
              scopeMrn={scope.mrn}
              vulnId={cve.id}
            />
          </Box>
        </Box>

        <ExposedAssets
          scope={scope}
          contextId={id}
          urlContextType={AssetContextualLinkType.Cve}
          filter={{
            mrn: String(cve.mrn),
            types: [FindingType.Cve],
            state: ScoreStateFilter.All,
          }}
          emptyStateMessage="There are currently no exposed assets for this CVE."
          onCreateTicketClick={handleCreateTicketDialogOpen}
          onCreatePullRequestClick={handleFindingsSecurityPipelinePRDialogOpen}
          onSetExceptionClick={handleSetExceptionDialogOpen}
          onRemoveExceptionClick={handleRemoveExceptionDialogOpen}
          selectionProps={{
            selectedEntities: selectedAffectedAssets,
            hasSelectPermissions: hasCreateTicketPermissions,
            onCheckboxClick: handleNodeClick,
            getIsRowSelected: (rowAssetMrn) => {
              return Boolean(
                selectedAffectedAssets.find(
                  (asset) => asset.mrn === rowAssetMrn,
                ),
              );
            },
            onCheckAll: (checked, edges) => {
              if (checked) {
                setSelectedAffectedAssets(
                  (edges || []).map((edge) => ({
                    groupId: "",
                    scopeMrn: edge?.mrn || "",
                    mrn: edge?.mrn,
                    findingMrn: cve.mrn,
                    title: cve.id,
                    exception: null,
                  })),
                );
              } else {
                setSelectedAffectedAssets([]);
              }
            },
            onCheckboxChange: async (
              e: ChangeEvent<HTMLInputElement>,
              checked: boolean,
              asset: ExposedAsset,
            ) => {
              const nextSelected = selectedAffectedAssets.filter(
                (s) => s.mrn !== asset?.assetMrn,
              );
              if (checked) {
                nextSelected.push({
                  mrn: asset?.assetMrn || "",
                  scopeMrn: asset?.assetMrn || "",
                  findingMrn: cve?.mrn || "",
                  title: cve.id,
                  exception: asset?.exception || null,
                });
              }

              setSelectedAffectedAssets(nextSelected);
            },
            onCancel: handleCancelClick,
          }}
        />
      </InventoryPageHeader>
    </>
  );
};
